import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProjectDto,
  TextDocumentDto,
  useCreateProjectTextDocumentMutation,
  useListTextDocumentsForProjectQuery,
  ViewDocumentAsOwnerDto,
} from "../../../autogen/bff-api";
import { CombinedDocuments } from "../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload } from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";
import { useEditor } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";

export const Documents = ({ projectId, refetch }: { projectId: string; refetch: () => void }) => {
  const displayer = useApiError();
  const { t } = useTranslation();
  const { state: project, updateField } = useEditor<ProjectDto>(projectId);

  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  const [createTextDocument] = useCreateProjectTextDocumentMutation();

  const { data: textDocRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForProjectQuery({
    projectId,
  });

  const { uppy, allFiles, documents, setDocuments, setUploads, removeUpload } = useUppyUploader({
    entityId: projectId,
    entityType: "Project",
    reloadEntity: refetch,
  });

  const documentsRef = useRef(project.documents);
  documentsRef.current = project.documents;

  const removeUploadedDocument = useCallback(
    async (document: ViewDocumentAsOwnerDto) => {
      updateField({ documents: (documentsRef.current ?? []).filter((d) => d.id !== document.id) });
    },
    [updateField]
  );

  useEffect(() => {
    setUploads(project.uploads ?? []);
  }, [project.uploads, setUploads]);

  useEffect(() => {
    setDocuments(project.documents ?? []);
  }, [project.documents, setDocuments]);

  useEffect(() => {
    setTextDocuments(textDocRes?.documents ?? []);
  }, [textDocRes?.documents]);

  return (
    <CombinedDocuments
      uploadedDocuments={documents}
      textDocuments={textDocuments}
      canEditDocumentNames={project.canEdit}
      canDeleteDocuments={project.canEdit}
      canEditTextDocuments={project.canEdit}
      createTextDocument={() =>
        createTextDocument({
          createProjectTextDocumentRequest: {
            name: "",
            text: "",
            projectId,
            tags: [],
          },
        })
          .unwrap()
          .catch((e) => {
            displayer.trigger(e);
            throw e;
          })
      }
      documentUploadConfig={
        project.canEdit
          ? {
              uppy,
              dropTargetId: "projectFileTarget",
              dropTargetHelperText: t("Upload relevant project documents"),
              uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
              removeUpload,
            }
          : undefined
      }
      isLoading={isLoadingTextDocuments}
      setUploadedDocuments={setDocuments}
      setTextDocuments={setTextDocuments}
      removeUploadedDocument={removeUploadedDocument}
      isTagsColumnVisible={true}
    />
  );
};
